import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { CreatePatientSchema } from "../../api/controllers/schemas/patients"
import { API_URL, QUERY_KEYS } from "../constants"
import { Patient, PatientWithLabs } from "../types/Patient"

export const useGetPatients = (orgId: string, token: string) =>
  useQuery<Patient[], Error>({
    queryKey: QUERY_KEYS.patients(orgId),
    queryFn: async () => {
      const res = await fetch(`${API_URL}/organizations/${orgId}/patients`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      })

      if (!res.ok) {
        const parsed = await res.json()
        throw new Error(parsed.error ?? parsed.message ?? parsed ?? "Internal Server Error")
      }

      const json = await res.json()
      if (json.error) {
        throw new Error(json.error)
      }
      return json?.patients ?? []
    },
  })

// Gets the patient by the internal MongoDB id
export const useGetPatient = (orgId: string, id: string, token: string) =>
  useQuery<PatientWithLabs, Error>({
    queryKey: QUERY_KEYS.patient(orgId, id),
    queryFn: async () => {
      const res = await fetch(`${API_URL}/organizations/${orgId}/patients/${id}/labs`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      })
      if (!res.ok) {
        const parsed = await res.json()
        throw new Error(parsed.error ?? parsed.message ?? parsed ?? "Internal Server Error")
      }

      const json = await res.json()
      if (json.error) {
        throw new Error(json.error)
      }

      return json
    },
  })

export const useCreatePatient = (orgId: string, token: string) => {
  const queryClient = useQueryClient()
  return useMutation<unknown, Error, CreatePatientSchema>({
    mutationFn: async (patient) => {
      const res = await fetch(`${API_URL}/organizations/${orgId}/patients`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(patient),
      })
      return await res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.patients(orgId) })
    },
  })
}

export const useEditPatient = (orgId: string, token: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (patient: Patient) => {
      const res = await fetch(`${API_URL}/organizations/${orgId}/patients/${patient._id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(patient),
      })
      return await res.json()
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.patients(orgId) })
    },
  })
}
